import { useTranslation } from "@refinedev/core";
import Button from "components/button";
import { CollapsibleSection } from "components/collapsibleSection";
import Input from "components/input";
import { IModalProps, Modal } from "components/modal";
import {
  IPhaseTicketItem,
  ISimpleEventDetail,
  ISubTicketItem,
  SupportedLanguage,
} from "interfaces";
import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";

interface IDuplicateTicketModalProps extends IModalProps {
  onAddTicket: (data: IPhaseTicketItem[]) => void;
  tickets: IPhaseTicketItem[];
  eventDetailData: ISimpleEventDetail;
}

export const DuplicateTicketModal = ({
  onClose,
  open,
  tickets,
  onAddTicket,
  eventDetailData,
}: IDuplicateTicketModalProps) => {
  const { translate } = useTranslation();
  const [selectedTickets, setSelectedTickets] = useState<IPhaseTicketItem[]>(
    tickets.map((ticket) => ({
      ...ticket,
      sub_tickets: [],
    }))
  );

  useEffect(() => {
    if (open) {
      setSelectedTickets(
        tickets.map((ticket) => ({
          ...ticket,
          sub_tickets: [],
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const _handleSelectTicket = (
    ticketIndex: number,
    subTicket: ISubTicketItem,
    isChecked: boolean
  ) => {
    let updatedSelectedTickets = [...selectedTickets];
    if (isChecked) {
      updatedSelectedTickets[ticketIndex].sub_tickets.push(subTicket);
    } else {
      updatedSelectedTickets[ticketIndex].sub_tickets = updatedSelectedTickets[
        ticketIndex
      ].sub_tickets.filter((ticket) => ticket.id !== subTicket.id);
    }
    setSelectedTickets(updatedSelectedTickets);
  };

  const _submit = () => {
    const submitTickets = selectedTickets.filter(
      (ticket) => ticket.sub_tickets.length > 0
    );
    onAddTicket(submitTickets);
  };

  const getSelectedSubTicketLength = (selectedTickets: IPhaseTicketItem[]) => {
    let subTicketCount = 0;
    for (let i = 0; i < selectedTickets.length; i++) {
      subTicketCount += selectedTickets[i].sub_tickets.length;
    }
    return subTicketCount;
  };

  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN)
    ? "en"
    : eventDetailData.languages[0] ?? "vi";

  return (
    <Modal
      title={translate("eventSetup.phaseAndTicket.modal.duplicateFromIndividual.title")}
      onClose={onClose}
      open={open}
      closeOnClickBackdrop={false}
    >
      <div className="flex flex-col gap-y-4 box-container w-full">
        <div className="flex flex-col justify-start items-start gap-x-2">
          <p className="text-sm">
            {translate("selected")}:{" "}
            {getSelectedSubTicketLength(selectedTickets)}
          </p>
        </div>
        {tickets.map((ticket, index) =>
          !isEmpty(ticket.ticket) ? (
            <div key={index} className="border rounded-lg border-gray-300">
              <CollapsibleSection
                label={get(ticket.ticket, `category.${firstLanguage}`, "")}
                defaultOpen={false}
              >
                <div className="box-container flex flex-col gap-y-4">
                  {(ticket.sub_tickets ?? []).map(
                    (subTicket, subTicketIndex) => (
                      <div
                        className="flex items-center justify-between px-4 py-4 border rounded-lg border-gray-300"
                        key={subTicket.id}
                      >
                        <p className="font-medium text-sm">
                          {get(subTicket, `type_name.${firstLanguage}`, "")}
                        </p>
                        <Input
                          type="checkbox"
                          label=""
                          id={`sub_ticket_${index}`}
                          checked={
                            selectedTickets[index].sub_tickets.find(
                              (selectedSubTicket) =>
                                selectedSubTicket.id === subTicket.id
                            )
                              ? true
                              : false
                          }
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            _handleSelectTicket(
                              index,
                              subTicket,
                              e.target.checked
                            )
                          }
                        />
                      </div>
                    )
                  )}
                </div>
              </CollapsibleSection>
            </div>
          ) : (
            <div className="flex flex-col gap-y-4" key={index}>
              {(ticket.sub_tickets ?? []).map((subTicket, subTicketIndex) => (
                <div
                  className="flex items-center justify-between px-4 py-4 border rounded-lg border-gray-300"
                  key={subTicket.id}
                >
                  <p className="font-medium text-sm">
                    {get(subTicket, `type_name.${firstLanguage}`, "")}
                  </p>
                  <Input
                    type="checkbox"
                    label=""
                    id={`sub_ticket_${index}`}
                    checked={
                      selectedTickets[index].sub_tickets.find(
                        (selectedSubTicket) =>
                          selectedSubTicket.id === subTicket.id
                      )
                        ? true
                        : false
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      _handleSelectTicket(index, subTicket, e.target.checked)
                    }
                  />
                </div>
              ))}
            </div>
          )
        )}
        <div className={`mt-4 self-end`}>
          <Button
            onClick={_submit}
            disabled={isEmpty(
              selectedTickets.find((ticket) => ticket.sub_tickets.length > 0)
            )}
          >
            {translate("duplicate")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
